/**
 * Textos legales de HelpyDoc (borrador base, ES).
 *
 * ⚠️ IMPORTANTE: esto es una PLANTILLA de partida generada para cubrir los
 * apartados que exige el RGPD. NO es asesoramiento legal y DEBE ser revisada y
 * completada por un profesional antes de considerarse definitiva. Los marcadores
 * `[PENDIENTE: …]` requieren datos reales del responsable del tratamiento.
 *
 * Última edición del borrador: 2026-07-27 — añadido el apartado «Datos de salud
 * conectados (Apple Salud y Health Connect)». Ese apartado NO es opcional: sin él
 * App Review rechaza la app por la guía 5.1.3 (una app que lee HealthKit debe
 * declarar en su política qué hace con esos datos), y la política de Health
 * Connect de Google Play exige lo equivalente. Sigue haciendo falta la revisión
 * legal de un profesional, que es un Tier-2 abierto.
 */

export interface LegalDoc {
  id: 'privacy' | 'terms' | 'disclaimer';
  title: string;
  updated: string;
  /** Cuerpo en Markdown ligero (## encabezados, - listas, párrafos). */
  body: string;
}

const UPDATED = '27 de julio de 2026 (borrador)';

export const PRIVACY_POLICY: LegalDoc = {
  id: 'privacy',
  title: 'Política de Privacidad',
  updated: UPDATED,
  body: `HelpyDoc es una aplicación de salud que trata datos personales, incluidos datos de categoría especial (datos de salud). Esta política explica qué datos tratamos, con qué fin y qué derechos tienes, conforme al Reglamento (UE) 2016/679 (RGPD).

## Responsable del tratamiento
[PENDIENTE: nombre/razón social del responsable], contacto: [PENDIENTE: email de contacto], dirección: [PENDIENTE: dirección].

## Qué datos tratamos
- Datos de cuenta: correo electrónico y datos de autenticación (a través de Google/Firebase).
- Datos de salud y nutrición: comidas registradas, fotos de tus platos, peso y objetivos.
- Datos de salud conectados, **solo si activas Salud conectada** y métrica a métrica: pasos, distancia, minutos activos, peso e hidratación procedentes de Apple Salud o Health Connect (ver el apartado propio más abajo).
- Datos técnicos: identificadores de sesión y registros mínimos de funcionamiento.

## Con qué finalidad y base legal
- Prestar el servicio (registrar y analizar tus comidas): ejecución del servicio que solicitas.
- Tratar datos de salud y almacenar tus fotos: **tu consentimiento explícito** (art. 9.2.a RGPD), que otorgas al iniciar y puedes revocar.

## Datos de salud conectados (Apple Salud y Health Connect)
Si activas **Salud conectada**, HelpyDoc lee de Apple Salud (iOS) o de Health Connect (Android) las métricas que tú autorices, una a una: **pasos, distancia, minutos activos, peso e hidratación**. La conexión es **voluntaria** y la app funciona sin ella.

- **Para qué se usan.** Únicamente para mostrarte tu propia evolución en «Informes › Progreso de salud» y para ajustar tus objetivos. Nada más.
- **Qué NO hacemos con ellos.** No se usan para **publicidad ni marketing**, no se ceden ni se venden a terceros, no se comparten con redes publicitarias, agentes de datos, aseguradoras ni empleadores, y no se emplean para elaborar perfiles ajenos a tu seguimiento de salud.
- **Base legal.** Tu **consentimiento explícito** (art. 9.2.a RGPD), que otorgas al conceder cada permiso.
- **Dónde se guardan.** En tu cuenta de HelpyDoc, con el mismo tratamiento que el resto de tus datos de salud. No se envían a los proveedores de IA: el análisis por IA se aplica a las fotos de tus comidas, no a estas métricas.
- **Cómo se revoca.** Desde el propio sistema (Ajustes de iOS › Salud › Apps, o la app Health Connect en Android) y también desde HelpyDoc, en **Ajustes › Salud conectada › Desconectar**. Al desconectar puedes además **borrar los datos ya importados**, y se eliminan.
- **Historial.** En Android, si lo autorizas, se puede leer el histórico anterior a la instalación para que tu evolución no empiece en blanco. Es un permiso aparte y puedes negarlo.

## Análisis por inteligencia artificial
Para estimar la composición nutricional de tus platos, las imágenes se procesan mediante modelos de IA de Google (Gemini vía Google Cloud / Vertex AI). Las estimaciones son **orientativas** y no constituyen consejo médico ni dietético profesional.

## Encargados y destinatarios
Usamos proveedores que actúan como encargados del tratamiento, principalmente Google Cloud Platform y Firebase (alojamiento, base de datos, autenticación e IA). [PENDIENTE: enumerar otros encargados si los hay, p. ej. USDA/OpenFoodFacts/FatSecret para búsqueda de alimentos].

## Transferencias internacionales
Algunos proveedores pueden tratar datos fuera del EEE. En tal caso se aplican las garantías previstas por el RGPD (p. ej. cláusulas contractuales tipo). [PENDIENTE: confirmar región de despliegue y garantías].

## Conservación
Conservamos tus datos mientras tengas cuenta activa. Puedes solicitar su supresión; las copias técnicas se eliminan en los plazos habituales de nuestros proveedores.

## Tus derechos
Puedes ejercer los derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad, así como retirar tu consentimiento en cualquier momento desde Ajustes o escribiendo a [PENDIENTE: email de contacto]. También puedes reclamar ante la autoridad de control competente (en España, la AEPD).

## Cambios
Podemos actualizar esta política; te informaremos de los cambios relevantes.`,
};

export const TERMS_OF_SERVICE: LegalDoc = {
  id: 'terms',
  title: 'Términos del Servicio',
  updated: UPDATED,
  body: `Al usar HelpyDoc aceptas estos términos.

## El servicio
HelpyDoc te ayuda a registrar comidas y obtener una **estimación orientativa** de su composición nutricional mediante inteligencia artificial. No es un producto sanitario ni sustituye la valoración de un profesional.

## Tu cuenta
Eres responsable de la actividad de tu cuenta y de la veracidad de los datos que introduces. No compartas tus credenciales.

## Uso aceptable
No uses la app con fines ilícitos, no subas contenido de terceros sin permiso ni intentes vulnerar su seguridad.

## Naturaleza de las estimaciones
Las cifras de calorías y macronutrientes son aproximaciones generadas por IA a partir de una imagen y pueden contener errores. Revísalas y ajústalas antes de tomar decisiones. No deben usarse como base única para decisiones clínicas.

## Limitación de responsabilidad
En la medida permitida por la ley, HelpyDoc se ofrece "tal cual", sin garantía de exactitud de las estimaciones. [PENDIENTE: revisar cláusula de limitación con asesoría legal según jurisdicción.]

## Cambios y terminación
Podemos modificar o interrumpir el servicio y actualizar estos términos. Puedes dejar de usar la app y solicitar la supresión de tus datos cuando quieras.

## Contacto
[PENDIENTE: email de contacto].`,
};

export const MEDICAL_DISCLAIMER: LegalDoc = {
  id: 'disclaimer',
  title: 'Aviso médico',
  updated: UPDATED,
  body: `HelpyDoc ofrece **estimaciones nutricionales orientativas** generadas por inteligencia artificial y herramientas de registro personal.

- **No sustituye** el consejo, diagnóstico ni tratamiento de un profesional sanitario o dietista-nutricionista.
- Las estimaciones de calorías y macronutrientes son aproximadas y pueden equivocarse; revísalas siempre.
- Ante cualquier decisión sobre tu salud, alimentación o tratamiento, **consulta con un profesional cualificado**.
- Si tienes una condición médica (diabetes, enfermedad renal, alergias, etc.), no te bases únicamente en esta app.

En caso de urgencia médica, contacta con los servicios de emergencia.`,
};

export const LEGAL_DOCS: LegalDoc[] = [PRIVACY_POLICY, TERMS_OF_SERVICE, MEDICAL_DISCLAIMER];

export function getLegalDoc(id: LegalDoc['id']): LegalDoc {
  return LEGAL_DOCS.find((d) => d.id === id) ?? PRIVACY_POLICY;
}
